import { computed, IObservableArray, makeAutoObservable, observable } from 'mobx'
import { type EditorView } from 'prosemirror-view'
import { LayerChange } from 'providers/LayerChangesProvider/types'
import { groupChangesByUuid } from 'common/Prose/Plugins/TrackChanges/utils'
import { NullDisposer } from './TrackedChanges/NullDisposer'
import { IDisposer } from './TrackedChanges/Types'
import { type DocStore } from 'stores/DocStore'
import { CollabItem, CollabItemType } from './DocStore.types'
import { CollabItemsStore as ICollabItemsStore } from 'common/Prose/Plugins/CollabItemWidget/Types'
import { CollabItemsStore } from 'stores/CollabItemsStore'
import { OrderedCollabItemsStore } from 'common/Prose/Plugins/CollabItemsExtractor/Types'

interface Props {
  disposer?: any
  trackingChanges?: boolean
  docStore?: DocStore
  collabItemsStore?: CollabItemsStore
  docChanged?: boolean
}

export class TrackedChanges implements ICollabItemsStore, OrderedCollabItemsStore {
  docStore?: DocStore
  hideDecorations: boolean = false
  trackingChanges: boolean = false
  disposer: IDisposer = new NullDisposer()
  collabItemsStore: CollabItemsStore
  docChanged: boolean = false

  private ungroupedChanges: IObservableArray<LayerChange> = observable.array()

  constructor({
    disposer = new NullDisposer(),
    trackingChanges = false,
    docStore,
    collabItemsStore = new CollabItemsStore(),
    docChanged = false,
  }: Props = {}) {
    makeAutoObservable(this, {
      previousChange: computed.struct,
      nextChange: computed.struct,
      changes: computed.struct,
    })

    this.disposer = disposer
    this.trackingChanges = trackingChanges
    this.docStore = docStore
    this.collabItemsStore = collabItemsStore
    this.docChanged = docChanged
  }

  setOrderedCollabItemsUuids(uuids: Set<string>) {
    this.collabItemsStore.setOrderedCollabItemsUuids(uuids)
  }

  accept = async (uuids: string[]) => {
    if (this.docStore?.inCleanupMode) this.docStore.cleanupModeStore?.clean(CollabItemType.TrackedChange)

    return this.disposer.accept(uuids).catch(() => {
      if (this.docStore?.inCleanupMode) this.docStore.cleanupModeStore?.dirty(CollabItemType.TrackedChange)
    })
  }

  acceptAll = async () => {
    if (this.docStore?.inCleanupMode) {
      this.docStore?.cleanupModeStore?.cleanMany(CollabItemType.TrackedChange, this.changesCount)
    }

    return this.disposer.accept(this.changes.map((change) => change.uuid)).catch(() => {
      if (this.docStore?.inCleanupMode) {
        this.docStore?.cleanupModeStore?.dirtyMany(CollabItemType.TrackedChange, this.changesCount)
      }
    })
  }

  reject = (uuid: string) => {
    if (this.docStore?.inCleanupMode) this.docStore.cleanupModeStore?.clean(CollabItemType.TrackedChange)

    return this.disposer.reject(uuid)
  }

  rejectAll = () => {
    if (this.docStore?.inCleanupMode) {
      this.docStore?.cleanupModeStore?.cleanMany(CollabItemType.TrackedChange, this.changesCount)
    }

    return this.disposer.rejectAll()
  }

  get changesCount() {
    return this.changes.length
  }

  setChanges(changes: LayerChange[]) {
    if (this.docStore?.inCleanupMode) {
      const {
        docStore: { cleanupModeStore },
      } = this
      const changesAccountedFor =
        cleanupModeStore.outstandingTrackedChangesCount - cleanupModeStore.cleanedUpTrackedChangesCount
      const newTrackedChanges = groupChangesByUuid(changes).length - changesAccountedFor
      this.docStore.cleanupModeStore.markManyForCleaning(CollabItemType.TrackedChange, newTrackedChanges)
    }
    this.ungroupedChanges.replace(changes)
  }

  setView(view?: EditorView) {
    this.disposer.setView(view)
  }

  select = (uuid?: string) => {
    this.collabItemsStore.select(uuid)

    if (this.collabItemsStore.selectedUuid && this.hideDecorations) {
      this.setHideDecorations(false)
    }
  }

  highlight = (uuid?: string) => {
    this.collabItemsStore.highlight(uuid)
  }

  selectPrevious = () => {
    if (!this.previousChange) {
      return
    }

    this.collabItemsStore.select(this.previousChange.uuid)
  }

  selectNext = () => {
    if (!this.nextChange) {
      return
    }

    this.collabItemsStore.select(this.nextChange.uuid)
  }

  get changes() {
    return groupChangesByUuid(this.ungroupedChanges)
  }

  setDocChanged = (docChanged: boolean) => {
    this.docChanged = docChanged
  }

  setHideDecorations = (hideDecorations: boolean) => {
    this.hideDecorations = hideDecorations
  }

  toggleDecorations = () => {
    this.setHideDecorations(!this.hideDecorations)
    this.select()
  }

  setTrackingChanges = (trackingChanges: boolean) => {
    this.trackingChanges = trackingChanges
  }

  setDocStore = (docStore: DocStore) => {
    this.docStore = docStore
  }

  get isDispositioning() {
    return this.disposer.isDispositioning
  }

  get canAcceptChanges() {
    return this.disposer.canAcceptChanges
  }

  get canRejectChanges() {
    return this.disposer.canRejectChanges
  }

  get readonly() {
    return this.disposer instanceof NullDisposer
  }

  get highlightedChange() {
    if (this.highlightedChangeIndex === -1) {
      return undefined
    }

    return this.changes[this.highlightedChangeIndex]
  }

  get selectedChange() {
    if (this.selectedChangeIndex === -1) {
      return undefined
    }

    return this.changes[this.selectedChangeIndex]
  }

  get previousChange() {
    if (this.selectedChangeIndex === 0) {
      return undefined
    }

    return this.changes[this.selectedChangeIndex - 1]
  }

  get nextChange() {
    if (this.selectedChangeIndex === this.changes.length - 1) {
      return undefined
    }
    return this.changes[this.selectedChangeIndex + 1]
  }

  get selectedChangeIndex() {
    return this.changes.findIndex((change) => change.uuid === this.collabItemsStore.selectedUuid)
  }

  get highlightedChangeIndex() {
    return this.changes.findIndex((change) => change.uuid === this.collabItemsStore.highlightedUuid)
  }

  get items() {
    return this.changes.map((change) => ({ type: CollabItemType.TrackedChange, item: change } as CollabItem))
  }

  get nextItem() {
    return this.collabItemFromChange(this.nextChange)
  }

  get previousItem() {
    return this.collabItemFromChange(this.previousChange)
  }

  get selectedItem() {
    return this.collabItemFromChange(this.selectedChange)
  }

  get highlightedItem() {
    return this.collabItemFromChange(this.highlightedChange)
  }

  get canAcceptItems() {
    return this.canAcceptChanges
  }

  get canRejectItems() {
    return this.canRejectChanges
  }

  private collabItemFromChange(change?: LayerChange) {
    if (!change) return undefined

    return { type: CollabItemType.TrackedChange, item: change } as CollabItem
  }

  get highlightedUuid() {
    return this.collabItemsStore.highlightedUuid
  }

  get selectedUuid() {
    return this.collabItemsStore.selectedUuid
  }
}
